/**
 * SMS Sent Messages Service
 * 
 * Manages access to SMS message history stored in Firestore.
 * This service provides functionality for:
 * - Retrieving sent SMS messages (all or by category)
 * - Getting message details by ID
 * - Sorting messages by date
 * - Supporting observable data for realtime updates
 * 
 * Works with the FirestorePathsService to access appropriate Firestore collections.
 */
import { Injectable } from '@angular/core';
import { 
    Firestore,
    collection,
    collectionData,
    // query,
    // orderBy,
    // startAt,
    // endAt,
    // doc,
    // getDoc
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
// import { map } from 'rxjs/operators';

import { FirestorePathsService } from './firestorePaths.service';
import { FirestoreService } from '../lib/firestore.service';
import type { TSmsSentMessage } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class SmsSentMessagesService {
    constructor(
        private firestore: Firestore,
        private firestoreService: FirestoreService, 
        private firestorePathsService: FirestorePathsService
    ) {}

    public getObservable(path: string): Observable<unknown> {   // Observable<TSmsSentMessage[]>
        let collectionPath: string;
        const category = '<fill in the category>';
        
        switch (path) {
            case 'root':
                collectionPath = this.firestorePathsService.getSmsSentMessagesPath();
                break;

            case 'all':
                collectionPath = this.firestorePathsService.getSmsSentMessagesAllRawPath();
                break;

            case 'category':
                collectionPath = this.firestorePathsService.getSmsSentMessagesCategoriesPath(category);
                break;

            default:
                throw new Error(`Invalid path: ${path}`);
        }

        const collectionRef = collection(this.firestore, collectionPath);
        return collectionData(collectionRef, { idField: 'uid' });
    }

    // TODO ok check if method works as expected
    public async getByUid(uid: string): Promise<TSmsSentMessage> {
        const path = this.firestorePathsService.getSmsSentMessagesAllRawPath();
        const response = await this.firestoreService.getByUid(path, uid);
        return response as TSmsSentMessage;
    }

    public async getAll(): Promise<TSmsSentMessage[]> {
        const path = this.firestorePathsService.getSmsSentMessagesPath();
        const response = await this.firestoreService.getAll(path);
        return response as TSmsSentMessage[];
    }

    // Uncomment and update when needed
    // https://stackoverflow.com/questions/41381948/firebase-query-for-data-within-a-date-range
    // public async getByDateRange(mmStartingDate: Date, mmEndingDate: Date): Promise<TSmsSentMessage[]> {
    //     try {
    //         const startingDate = mmStartingDate.valueOf();
    //         const endingDate = mmEndingDate.valueOf();
    //
    //         const collectionPath = this.firestorePathsService.getSmsSentMessagesAllRawPath();
    //         const collectionRef = collection(this.firestore, collectionPath);
    //         const q = query(
    //             collectionRef, 
    //             orderBy('sentDate'), 
    //             startAt(startingDate), 
    //             endAt(endingDate)
    //         );
    //         
    //         const response = await getDocs(q);
    //         
    //         const messages: TSmsSentMessage[] = [];
    //         response.forEach((doc) => {
    //             const message = doc.data() as TSmsSentMessage;
    //             message.uid = doc.id;
    //             messages.push(message);
    //         });
    //         
    //         return messages;
    //     } catch (err) {
    //         console.error('Error retrieving messages by date range:', err);
    //         throw err;
    //     }
    // }

    public sort(messages: TSmsSentMessage[], sortAscending = true): TSmsSentMessage[] {
        const sortedMessages = messages.sort((a, b) => {
            const aSms = a?.sentToCustomerDate;
            const bSms = b?.sentToCustomerDate;

            if (!aSms || !bSms) {
                return 0;
            }

            // This is a reverse sort
            if (aSms < bSms) {
                // return -1;
                return (sortAscending ? -1 : 1);
            } else if (aSms > bSms) {
                // return 1;
                return (sortAscending ? 1 : -1);
            } else {
                return 0;
            }
        });
        return sortedMessages;
    }

    // Uncomment and update when needed
    // private async getMessagesByDateRange(path: string, startingDate: Date, endingDate: Date) {
    //     const collectionPath = this.firestorePathsService.getSmsSentMessagesPath();
    //     const collectionRef = collection(this.firestore, collectionPath);
    //     const q = query(
    //         collectionRef, 
    //         orderBy('smsSentDate'), 
    //         startAt(startingDate.valueOf()), 
    //         endAt(endingDate.valueOf())
    //     );
    //     const snapshot = await getDocs(q);
    //     
    //     const messages: TSmsSentMessage[] = [];
    //     snapshot.forEach((doc) => {
    //         const message = doc.data() as TSmsSentMessage;
    //         message.uid = doc.id;
    //         messages.push(message);
    //     });
    //     
    //     return messages;
    // }
}